import { useState } from 'react'
import { useI18n } from '../i18n'
import { Banner, SupportCard } from '../components/bits'
import { ShareBox } from '../components/ShareBox'
import { api } from '../lib/api'
import { sessionContext } from '../lib/flow'
import { setPost } from '../lib/journal'
import { useSession } from '../store/session'

type QA = { q: string; a: string | null }

export function Result() {
  const { t } = useI18n()
  const s = useSession()
  const [post, setPostText] = useState(s.postDraft)
  const [saved, setSaved] = useState(false)
  const [question, setQuestion] = useState('')
  const [qa, setQa] = useState<QA[]>([])
  const [history, setHistory] = useState<{ role: 'user' | 'assistant'; content: string }[]>([])

  const mb = s.metricsBefore
  const me = s.metricsEx ?? { sharpPct: 0, speedCV: 0 }
  const deltaSharp = mb ? mb.sharpPct - me.sharpPct : null

  const savePost = () => {
    s.set({ postDraft: post })
    if (s.entryId) setPost(s.entryId, post)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const ask = async () => {
    const q = question.trim()
    if (!q) return
    setQuestion('')
    setQa((prev) => [...prev, { q, a: null }])
    try {
      const res = await api.ask({
        before: s.beforePNG!,
        after: s.afterPNG,
        context: sessionContext(),
        history,
        question: q,
      })
      setHistory((h) => [...h, { role: 'user', content: q }, { role: 'assistant', content: res.answer }])
      setQa((prev) => prev.map((x, i) => (i === prev.length - 1 ? { ...x, a: res.answer } : x)))
    } catch {
      setQa((prev) => prev.map((x, i) => (i === prev.length - 1 ? { ...x, a: t.result.aiFailed } : x)))
    }
  }

  const rows: [string, string, boolean][] = [
    [t.result.dlCheckin, t.result.dlCheckinVal(s.mood, s.energy), true],
    [t.result.dlSharp, mb ? `${mb.sharpPct}% → ${me.sharpPct}%` : `— → ${me.sharpPct}%`, !mb || me.sharpPct <= mb.sharpPct],
    [t.result.dlCV, mb ? `${mb.speedCV} → ${me.speedCV}` : `— → ${me.speedCV}`, !mb || me.speedCV <= mb.speedCV],
    [t.result.dlArcs, String(s.calmArcs), true],
  ]

  return (
    <section className="screen">
      <h2>{t.result.title}</h2>
      {s.aiFailed && <Banner text={t.errors.aiFallback} />}
      <div className="pair">
        <figure>
          <img src={s.beforePNG ?? ''} alt={t.result.before} />
          <figcaption>{t.result.before}</figcaption>
        </figure>
        <figure>
          <img src={s.afterPNG ?? ''} alt={t.result.after} />
          <figcaption>{t.result.after}</figcaption>
        </figure>
      </div>

      <div className="card">
        <h3>{t.result.finalCard}</h3>
        <p>{s.finalText}</p>
        <span className="state-pill">{t.result.statePrefix}{s.finalState}</span>
      </div>

      {s.support && <SupportCard />}

      <div className="card">
        <h3>{t.result.postCard}</h3>
        <textarea
          className="feel"
          placeholder={t.result.postPlaceholder}
          value={post}
          onChange={(e) => setPostText(e.target.value)}
        />
        <div className="btn-row" style={{ marginTop: 10 }}>
          <button className="btn btn-ghost" style={{ padding: 11 }} onClick={savePost}>
            {t.result.savePost}
          </button>
        </div>
        {saved && <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>{t.result.postSaved}</p>}
      </div>

      <div className="card">
        <h3>{t.result.askCard}</h3>
        <div className="ask">
          <input
            placeholder={t.result.askPlaceholder}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && ask()}
          />
          <button onClick={ask}>→</button>
        </div>
        {qa.length > 0 && (
          <div>
            {qa.map((x, i) => (
              <div key={i}>
                <p className="qa-q">— {x.q}</p>
                {x.a === null ? (
                  <p className="qa-a thinking dots-anim">{t.result.aiWriting}</p>
                ) : (
                  <p className="qa-a">{x.a}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ShareBox img={s.afterPNG!} state={s.finalState} before={s.beforePNG} deltaSharp={deltaSharp} />

      <div className="btn-row col">
        <button className="btn btn-ghost" onClick={() => { s.set({ drawOver: true }); s.go('exercise') }}>
          {t.result.drawOver}
        </button>
        <button className="btn btn-ghost" onClick={() => { s.reset(); s.go('home') }}>
          {t.result.newSession}
        </button>
      </div>

      <details className="stats">
        <summary>{t.result.stats}</summary>
        <div className={'delta-big' + (deltaSharp !== null && deltaSharp < 0 ? ' neg' : '')}>
          <b>
            {deltaSharp === null
              ? s.calmArcs
              : `${deltaSharp > 0 ? '−' : '+'}${Math.abs(deltaSharp)} pp`}
          </b>
          <span>{deltaSharp === null ? t.result.deltaArcs : t.result.deltaSharp}</span>
        </div>
        <div style={{ marginTop: 6 }}>
          {rows.map(([n, v, good]) => (
            <div className="dl" key={n}>
              <span>{n}</span>
              <b className={good ? 'up' : 'dn'}>{v}</b>
            </div>
          ))}
        </div>
      </details>
    </section>
  )
}
